import {
  storeHomeNVClient,
  resolve,
  doorClient,
  omsV2Client,
  ImResponse,
  leyanbotClient,
} from 'services/api';
import { isUndefined, map } from 'lodash-es';
import type {
  BootInProcess,
  GetUnConfigBootStrapStoreInfoReturn,
  LeftTopNoticeReturn,
  GetConfigurationUnsetStoreData,
  GetUnConfigBootStrapStoreInfoParams,
  GetConfigurationUnsetStoreReturn,
  GetConfigurationScenarioTemplateParams,
  GetConfigurationScenarioTemplateReturn,
  PostBootProcessingData,
  PostBootProcessingReturn,
  PostConfigurationPolicyData,
  RecommendStoreReturn,
  GetStoreScenarioReturn,
  PostConfigurationReuseData,
  PostEnableConfigBootstrap,
  PostInitScenarioPolicyData,
} from './types/api';

export const getBootProcessing = (aiEnable?: boolean, signal?: AbortSignal) => {
  return resolve<BootInProcess>(storeHomeNVClient.get('/bootstrap/inprogress', { signal })).then(
    (res) => {
      if (!aiEnable) {
        return res;
      }
      return res.filter(
        ({ configType }) => !['BASIC', 'AI_MODEL', 'ADVANCED'].includes(configType),
      );
    },
  );
};

/**
 * 暂存进行中的配置引导
 * https://app.apifox.com/link/project/625489/apis/api-137267738
 */
export const postBootProcessing = (data: PostBootProcessingData) => {
  return resolve<PostBootProcessingReturn>(storeHomeNVClient.post('/bootstrap/inprogress', data));
};

export const deleteBootProcessing = (id: string | number) => {
  return storeHomeNVClient.post(`/bootstrap/delete/${id}`, null);
};

/**
 * 未完成配置引导的提示信息
 */
export const getLeftTopNotice = (aiEnable?: boolean, signal?: AbortSignal) => {
  if (aiEnable) {
    return resolve(storeHomeNVClient.get<any>('/notice/left_top_notice/v2', { signal })).then(
      ({ configureBootstrap }: any) => {
        return {
          configureBootstrap: {
            unConfigBasicCount:
              configureBootstrap.unConfigMarketingCount + configureBootstrap.unConfigSaleBotCount,
            unConfigAIModelCount: configureBootstrap.unConfigAssistantBotCount,
            unConfigAdvancedCount: configureBootstrap.unConfigSaleBotAdvancedCount,
          },
        };
      },
    );
  }
  return resolve<LeftTopNoticeReturn>(
    storeHomeNVClient.get('/notice/left_top_notice', { signal }),
  ).then(({ configureBootstrap }) => {
    return {
      configureBootstrap: {
        ...configureBootstrap,
        unConfigAIModelCount: 0,
      },
    };
  });
};

/**
 * 查询基础/进阶配置的未进行配置引导的店铺信息
 */
export const getUnConfigBootStrapStoreInfo = (
  params: GetUnConfigBootStrapStoreInfoParams,
): Promise<GetUnConfigBootStrapStoreInfoReturn> => {
  return resolve<GetUnConfigBootStrapStoreInfoReturn>(
    doorClient.get('/overseaim-store-home/unConfigBootStrapStoreInfo', {
      params,
    }),
  ).then((res) => {
    if (params.type !== 7) {
      return res;
    }
    return getUnConfigBootStrapStoreInfo({ type: 5 }).then((saleBotRes) => {
      return res.filter(({ storeId }) => saleBotRes.every((_) => _.storeId !== storeId));
    });
  });
};

/**
 * 查询对应场景未配置的店铺信息
 * https://app.apifox.com/link/project/625489/apis/api-137080625
 */
export const getConfigurationUnsetStore = (data: GetConfigurationUnsetStoreData) => {
  return resolve<GetConfigurationUnsetStoreReturn>(
    omsV2Client.post('/configuration/unsetStore', data),
  );
};

/**
 * 查询对应场景默认配置话术
 * https://app.apifox.com/link/project/625489/apis/api-137032109
 */
export const getConfigurationScenarioTemplate = (
  params: GetConfigurationScenarioTemplateParams,
) => {
  return resolve<GetConfigurationScenarioTemplateReturn>(
    omsV2Client.post('/configuration/scenarioTemplate', params),
  );
};

function takeoutTrans(data: PostConfigurationPolicyData, enable: boolean) {
  const { scenarios, ...rest } = data;
  return {
    scenarios: map(scenarios, (scenario) => {
      const { regionPolicyList, ...rest } = scenario;
      return {
        regionPolicyList: map(regionPolicyList, (regionPolicy) => {
          const { executeContext, enable: _enable, ...rest } = regionPolicy;
          return {
            executeContext: {
              content: executeContext.content,
              translatedContent: '',
            },
            enable: isUndefined(enable) ? _enable : enable,
            ...rest,
          };
        }),
        ...rest,
      };
    }),
    ...rest,
  };
}
/**
 * 保存和更新策略
 * https://app.apifox.com/link/project/625489/apis/api-137054301
 */
export const postConfigurationPolicy = (data: PostConfigurationPolicyData, enable = true) => {
  const takeoutedTrans = takeoutTrans(data, enable);
  return omsV2Client.post('/configuration/policy', takeoutedTrans);
};

/**
 * 获取推荐店铺
 * https://app.apifox.com/link/project/625489/apis/api-138087572
 */
export const getRecommendStore = () => {
  return resolve<RecommendStoreReturn>(storeHomeNVClient.get('/bootstrap/get_recommended_store'));
};

/**
 * 获取店铺的场景配置
 * https://app.apifox.com/link/project/625489/apis/api-141419634
 */
export const getStoreScenario = (params: { storeId: string }) => {
  return resolve<GetStoreScenarioReturn>(
    omsV2Client.get('/configuration/storeScenario', { params }),
  );
};

/**
 * 复用场景配置
 * https://app.apifox.com/link/project/625489/apis/api-141427076
 */
export const configurationReuse = (data: PostConfigurationReuseData) => {
  return omsV2Client.post('/configuration/reuse', data);
};

/**
 * 复用时根据目标店铺更新配置
 * https://app.apifox.com/link/project/625489/apis/api-143296204
 */
export const enableConfigBootstrap = (data: PostEnableConfigBootstrap) => {
  return storeHomeNVClient.post('/bootstrap/enable_config_bootstrap', data);
};

/**
 * 初始化场景策略
 */
export const initScenarioPolicy = (data: PostInitScenarioPolicyData) => {
  return omsV2Client.post('/configuration/policy/init', data);
};

export const getDefaultValueRule = () => {
  return resolve(
    leyanbotClient.get<
      ImResponse<{
        ruleMap: {
          LOGISTICS_SEND_OUT_TIME: {
            TODAY_OR_TOMORROW_ON_TIME: string;
            RANGE: string;
          };
          LOGISTICS_SEND_OUT_PLACE: {
            SEND_OUT_PLACE_OF_AREA: string;
          };
          LOGISTICS_DELIVERED_IN_TIME: {
            DELIVERED_RANGE_OF_AREA: string;
            DELIVERED_RANGE: string;
          };
          SHOP_CUSTOMER_SERVICE_WORKING_TIME: {
            WORK_TIME: string;
          };
        };
      }>
    >('/oversea-knowledge/config_bootstrap/default_value_rule'),
  ).then((res) => {
    const {
      ruleMap: {
        LOGISTICS_SEND_OUT_TIME: { TODAY_OR_TOMORROW_ON_TIME, RANGE },
        LOGISTICS_DELIVERED_IN_TIME: { DELIVERED_RANGE_OF_AREA, DELIVERED_RANGE },
        LOGISTICS_SEND_OUT_PLACE: { SEND_OUT_PLACE_OF_AREA },
        SHOP_CUSTOMER_SERVICE_WORKING_TIME: { WORK_TIME },
      },
    } = res;
    return {
      LOGISTICS_SEND_OUT_TIME: {
        TODAY_OR_TOMORROW_ON_TIME: JSON.parse(TODAY_OR_TOMORROW_ON_TIME),
        RANGE: JSON.parse(RANGE),
      },
      LOGISTICS_DELIVER: {
        DESIGNATE_AREA: JSON.parse(DELIVERED_RANGE_OF_AREA),
        RANGE: JSON.parse(DELIVERED_RANGE),
      },
      SHOP_CUSTOMER_SERVICE_WORKING_TIME: {
        WORK_TIME: JSON.parse(WORK_TIME),
      },
      LOGISTICS_SEND_OUT_PLACE: {
        SEND_OUT_PLACE_OF_AREA: JSON.parse(SEND_OUT_PLACE_OF_AREA),
      },
    } as BootGuide.AiModel.DefaultValueRule;
  });
};

export const updateShippingDays = (values: BootGuide.AiModel.FormValues.ShippingDays[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/LOGISTICS_SEND_OUT_TIME',
    {
      sendOutItems: values.map(
        ({
          storeScopes,
          valueType,
          todayOrTomorrowTime,
          spotRangeMin,
          spotRangeMax,
          preSaleRangeMin,
          preSaleRangeMax,
        }) => {
          if (valueType === 'time') {
            return {
              storeScopes,
              todayOrTomorrowTime,
            };
          }
          if (valueType === 'range') {
            return {
              storeScopes,
              spotRangeMin,
              spotRangeMax,
              preSaleRangeMin,
              preSaleRangeMax,
            };
          }
          return {
            storeScopes,
          };
        },
      ),
    },
  );
};

export const updateTransitDays = (values: BootGuide.AiModel.FormValues.TransitDays[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/LOGISTICS_DELIVERED_IN_TIME',
    {
      transportationItems: values.map(
        ({
          storeScopes,
          valueType,
          range: { min: rangeMin, max: rangeMax },
          areas,
          transports,
        }) => {
          if (valueType === 'range') {
            return {
              storeScopes,
              rangeMin,
              rangeMax,
            };
          }
          if (valueType === 'area') {
            return {
              storeScopes,
              areaRanges: areas,
            };
          }
          if (valueType === 'transport') {
            return {
              storeScopes,
              deliveryMethodRanges: transports,
            };
          }
          return {
            storeScopes,
          };
        },
      ),
    },
  );
};

export const updateSendPhotoBeforeShipping = (
  values: BootGuide.AiModel.FormValues.SendPhotoBeforeShipping[],
) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/LOGISTICS_SEND_PICTURES_BEFORE',
    values.reduce(
      (res, { value, storeScopes }) => {
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        if (value === 'support') {
          res.supportStoreScopes = storeScopes;
        }
        if (value === 'uncertain') {
          res.uncertainStoreScopes = storeScopes;
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
        supportStoreScopes: [],
        uncertainStoreScopes: [],
      } as any,
    ),
  );
};

export const updateCombinedShipping = (values: BootGuide.AiModel.FormValues.CombinedShipping[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/LOGISTICS_CONSOLIDATED_MULTIPLE_ORDER',
    values.reduce(
      (res, { value, storeScopes }) => {
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        if (value === 'support') {
          res.supportStoreScopes = storeScopes;
        }
        if (value === 'uncertain') {
          res.uncertainStoreScopes = storeScopes;
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
        supportStoreScopes: [],
        uncertainStoreScopes: [],
      } as any,
    ),
  );
};

export const updateFreeShipping = (values: BootGuide.AiModel.FormValues.FreeShipping[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/SHOP_FREE_SHIPPING',
    {
      freeShippingItems: values,
    },
  );
};

export const updateProvideInvoice = (values: BootGuide.AiModel.FormValues.ProvideInvoice[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/SHOP_INVOICE',
    values.reduce(
      (res, { value, storeScopes }) => {
        if (value === 'invoice') {
          res.invoiceStoreScopes = storeScopes;
        }
        if (value === 'receipt') {
          res.receiptStoreScopes = storeScopes;
        }
        if (value === 'invoiceAndReceipt') {
          res.invoiceAndReceiptStoreScopes = storeScopes;
        }
        if (value === 'none') {
          res.noneStoreScopes = storeScopes;
        }
        return res;
      },
      {
        receiptStoreScopes: [],
        invoiceStoreScopes: [],
        invoiceAndReceiptStoreScopes: [],
        noneStoreScopes: [],
      } as any,
    ),
  );
};

export const updateDropsShipping = (values: BootGuide.AiModel.FormValues.DropsShipping[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/SHOP_DROPSHIPPING',
    values.reduce(
      (res, { value, storeScopes, dropShippingType }) => {
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        if (value === 'support') {
          res.dropShippingDTOList.push({
            storeScopes,
            dropShippingType,
          });
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
        dropShippingDTOList: [],
      } as any,
    ),
  );
};

export const updatePickUp = (values: BootGuide.AiModel.FormValues.PickUp[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/SHOP_PICKUP_AT_HOME',
    values.reduce(
      (res, { value, storeScopes }) => {
        if (value === 'support') {
          res.supportStoreScopes = storeScopes;
        }
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
        supportStoreScopes: [],
      } as any,
    ),
  );
};

export const updateSpecifyExpressType = (
  values: BootGuide.AiModel.FormValues.SpecifyExpressType[],
) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/LOGISTICS_DELIVERY_COURIER_TYPE',
    values.reduce(
      (res, cur) => {
        const { storeScopes, value, deliveryTypes } = cur;
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        if (value === 'support') {
          res.supportDeliveryTypeList.push({
            supportStoreScopes: storeScopes,
            deliveryTypes,
          });
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
        supportDeliveryTypeList: [],
      } as any,
    ),
  );
};

export const updateServiceWorkingTime = (
  values: BootGuide.AiModel.FormValues.ServiceWorkingTime[],
) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/SHOP_CUSTOMER_SERVICE_WORKING_TIME',
    {
      customerWorkTimeItems: values,
    },
  );
};

export const updateWarranty = (values: BootGuide.AiModel.FormValues.Warranty[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/ITEM_WARRANTY_SERVICE',
    values.reduce(
      (res, cur) => {
        const { storeScopes, value, number, unit } = cur;
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        if (value === 'support') {
          res.supportWarrantyServiceList.push({
            storeScopes,
            number,
            unit,
          });
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
        supportWarrantyServiceList: [],
      } as any,
    ),
  );
};

export const updateShelfLife = (values: BootGuide.AiModel.FormValues.ShelfLife[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/ITEM_EXPIRATION_DATE',
    values.reduce(
      (res, cur) => {
        const { storeScopes, value, number, unit } = cur;
        if (value === 'noSupport') {
          res.expirationItems.push({
            noSupportStoreScopes: storeScopes,
          });
        }
        if (value === 'support') {
          res.expirationItems.push({
            expirationStoreScopes: storeScopes,
            number,
            unit,
          });
        }
        return res;
      },
      { expirationItems: [] } as any,
    ),
  );
};

export const updatePrivateShipping = (values: BootGuide.AiModel.FormValues.PrivateShipping[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/OTHER_PRIVATE_SHIPPING',
    values.reduce(
      (res, { value, storeScopes, items }) => {
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        if (value === 'support') {
          res.supportPrivateShippingList.push({
            storeScopes,
            items,
          });
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
        supportPrivateShippingList: [],
      } as any,
    ),
  );
};

export const updateGift = (values: BootGuide.AiModel.FormValues.Gift[]) => {
  return leyanbotClient.post('/oversea-knowledge/config_bootstrap/update_value/OTHER_GIFT', {
    giftItems: values.map(({ currencyAmountList, ...others }) => {
      return {
        ...others,
        currencyAmountList: currencyAmountList.map(({ region, ...others }) => {
          return others;
        }),
      };
    }),
  });
};

export const updateWholesale = (values: BootGuide.AiModel.FormValues.Wholesale[]) => {
  return leyanbotClient.post(
    '/oversea-knowledge/config_bootstrap/update_value/SHOP_WHOLESALE',
    values.reduce(
      (res, { value, storeScopes }) => {
        if (value === 'noSupport') {
          res.noSupportStoreScopes = storeScopes;
        }
        return res;
      },
      {
        noSupportStoreScopes: [],
      } as any,
    ),
  );
};

export const getAllKnowledge = () => {
  return resolve(
    leyanbotClient.get<ImResponse<any>>('/oversea-knowledge/config_bootstrap/template_value'),
  );
};
